/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { plugins as pluginsMetadata } from '@ckeditor/ckeditor5-metadata';
import { keyBy, merge, values } from 'lodash';

import type { ViewPlugin } from '../types/ViewPlugin';
import pluginViewMetadata from './pluginsViewMetadata.json';

const mergedMetadata = merge(
	{},
	keyBy( pluginsMetadata, 'className' ),
	keyBy( pluginViewMetadata, 'className' )
);

const tempAllPlugins = values<ViewPlugin>( mergedMetadata );

copyAIAssistantComponentsToAdapters( tempAllPlugins );
removeUIComponentsFromImagePlugins( tempAllPlugins );

export const plugins = tempAllPlugins;

/**
 * `AIAssistant` is a hidden plugin required by both AI adapters.
 * Also, `AIAssistant` defines UI components. They must be copied into adapters to display AI icons
 * on the "Features" view.
 */
function copyAIAssistantComponentsToAdapters( allPlugins: Array<ViewPlugin> ): void {
	const aiAssistantPlugin = allPlugins.find( plugin => plugin.className === 'AIAssistant' )!;
	const openAITextAdapterPlugin = allPlugins.find( plugin => plugin.className === 'OpenAITextAdapter' )!;
	const awsTextAdapterPlugin = allPlugins.find( plugin => plugin.className === 'AWSTextAdapter' )!;

	openAITextAdapterPlugin.uiComponents = aiAssistantPlugin.uiComponents.slice();
	awsTextAdapterPlugin.uiComponents = aiAssistantPlugin.uiComponents.slice();
	aiAssistantPlugin.uiComponents = [];
}

/**
 * Both of the `Image*` plugins (via `ckeditor5-metadata.json`) register the same UI component called `imageTextAlternative`,
 * while the `ImageTextAlternative` plugin delivers it and is not mentioned in the metadata files.
 *
 * The `@ckeditor/ckeditor5-metadata` package fixes that and adds the proper entries in the `ImageTextAlternative` plugin.
 *
 * This function aims to clean Image's `uiComponents` array because it is registered thrice now.
 */
function removeUIComponentsFromImagePlugins( allPlugins: Array<ViewPlugin> ): void {
	const pluginsToClean = [ 'ImageBlock', 'ImageInline' ];

	for ( const pluginName of pluginsToClean ) {
		const viewPlugin = allPlugins.find( plugin => plugin.className === pluginName )!;

		viewPlugin.uiComponents = [];
	}
}
